import React, { useContext, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useCookies } from 'react-cookie';
import styled from 'styled-components';
import color from 'styles/colors';
import { respondTo } from 'styles/helper';
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { Profile } from 'lib/types/profileType';
import { profileConfig } from 'lib/utils/configs';
import { setConfigToProfile, tokenValid } from 'lib/utils/helper';
import { handleTrackEvent, Events } from 'lib/analytics/trackCalls';
import { AppContext } from 'context/GlobalContextProvider';
import { setError, setFlowStage } from 'context/state/reducer';
import { Loader } from 'components/common';
import {
  ACCOUNT_COOKIE_NAME_FOX,
  COOKIE_CODE_INFO,
  FLOW_STAGE_MVPD_LINK,
  ROUTE_HOME,
  REDIRECT_SECONDS,
} from 'constants/index';

export type Props = {
  profile: Profile;
};

const Success = ({ profile }: Props): JSX.Element | null => {
  const { state, dispatch } = useContext(AppContext);
  const router = useRouter();
  const [cookies] = useCookies([ACCOUNT_COOKIE_NAME_FOX, COOKIE_CODE_INFO]);

  useEffect(() => {
    if (!profile?.checkAdobeAuthN) return;
    let updatedProfile;
    let successTimer: NodeJS.Timeout;

    const validateAuth = async () => {
      updatedProfile = (await setConfigToProfile(profile, {
        appName: profileConfig.appName,
        appVersion: profileConfig.appVersion,
        accessToken: cookies[ACCOUNT_COOKIE_NAME_FOX]?.accessToken,
      })) as Profile;

      const accessToken = jwtDecode<JwtPayload>(cookies[ACCOUNT_COOKIE_NAME_FOX]?.accessToken);

      try {
        if (tokenValid(accessToken)) {
          const codeInfoCookie = cookies[COOKIE_CODE_INFO] ?? {};
          const firstScreenDeviceId = codeInfoCookie.firstScreenDeviceId;
          const authNResponse = await updatedProfile?.checkAdobeAuthN?.({
            deviceId: firstScreenDeviceId,
          });
          const { accessToken, mvpd } = authNResponse;

          if (accessToken || mvpd) {
            const redirect = async () => {
              await router.push(
                process.env.NEXT_PUBLIC_DELTA_SITE_URL || (state?.config?.siteUrl as string),
              );
            };
            successTimer = setTimeout(redirect, REDIRECT_SECONDS * 1000);
            dispatch(setFlowStage(FLOW_STAGE_MVPD_LINK));
            handleTrackEvent({
              eventName: Events.PROFILE_ACCESS_ACTIVATION_COMPLETED,
            });
            return;
          } else {
            await router.push(ROUTE_HOME);
          }
        }
        return;
      } catch (e) {
        dispatch(setError(e));
        await router.push(ROUTE_HOME);
      }
    };
    validateAuth();
    return () => {
      clearTimeout(successTimer);
    };
  }, [cookies, dispatch, profile, router, state?.config?.siteUrl]);

  return (
    <StyledSuccess>
      <h1>
        Thank you for signing in <br />
        with your TV Provider.
      </h1>
      <p>
        You may now return to your TV to begin your <br />
        enhanced streaming experience.
      </p>
      <Loader />
    </StyledSuccess>
  );
};

export default Success;

const StyledSuccess = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 3rem;
  margin: 0 auto;
  height: 100%;
  font-size: 16px;
  text-align: center;
  ${respondTo.bp_767`
    max-width: 500px;
    margin: auto;
    padding-top: 6rem;
  `}
  h1 {
    font-family: 'Graphik', serif;
    font-size: 30px;
    font-weight: 600;
    letter-spacing: -0.38px;
    line-height: 37px;
    text-align: center;
  }

  p {
    margin: 0 0 1.5rem;
    color: ${color.white25};
    font-family: 'Graphik', serif;
    font-size: 24px;
    font-weight: 300;
    font-style: normal;
    letter-spacing: -0.3px;
    line-height: 30px;
    opacity: 0.5;
  }
`;
